import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../app/providers/AuthProvider";
import { AuthProviderButtons } from "./AuthProviderButtons";
import { Icon } from "../../components/Icon";
import { ApiError } from "../../lib/api-client";

type EmailMode = "login" | "register";

const INVALID_CREDENTIALS_ERROR = "Неверный email или пароль.";
const REGISTRATION_ERROR =
  "Не удалось создать аккаунт. Проверьте данные или попробуйте войти.";
const REQUEST_ERROR = "Не удалось войти. Попробуйте ещё раз.";

export function AuthPage() {
  const { isAuthenticated, isLoading, authError } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas">
        <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-action border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-5 py-8 lg:p-10">
      <div className="hidden min-h-[680px] w-full max-w-[1120px] overflow-hidden rounded-[40px] bg-[#171714] shadow-[0_32px_90px_rgba(36,33,23,0.18)] lg:grid lg:grid-cols-[1.15fr_0.85fr]">
        <section className="relative flex flex-col justify-between overflow-hidden p-14 text-white">
          <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-action opacity-90" />
          <div className="absolute bottom-[-12rem] left-[-8rem] h-[30rem] w-[30rem] rounded-full border-[80px] border-white/[0.05]" />
          <div className="relative flex items-center gap-3 text-lg font-extrabold">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-action text-white"><Icon name="gift" size={23} /></span>
            Family Gifts
          </div>
          <div className="relative max-w-lg">
            <p className="mb-5 text-xs font-bold uppercase tracking-[0.24em] text-[#FF9C40]">Подарки без спойлеров</p>
            <h2 className="text-6xl font-extrabold leading-[0.96] tracking-[-0.065em]">Радуйте близких. Вместе.</h2>
            <p className="mt-7 max-w-md text-base leading-7 text-white/55">Собирайте желания семьи в одном месте и выбирайте подарки без повторов.</p>
          </div>
          <p className="relative text-xs text-white/30">Одно пространство для самых близких</p>
        </section>

        <section className="flex items-center justify-center bg-white p-12">
          <LoginPanel authError={authError} />
        </section>
      </div>

      <div className="flex w-full max-w-sm flex-col items-center rounded-[32px] bg-white px-6 py-9 shadow-[0_24px_70px_rgba(36,33,23,0.1)] lg:hidden">
        <LoginPanel authError={authError} showLogo />
      </div>
    </div>
  );
}

function LoginPanel({ authError, showLogo = false }: { authError: string | null; showLogo?: boolean }) {
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailMode, setEmailMode] = useState<EmailMode>("login");

  return (
    <div className="flex w-full max-w-sm flex-col items-center">
      {showLogo && (
        <div className="mb-7 flex h-20 w-20 items-center justify-center rounded-[25px] bg-content text-action shadow-[0_14px_28px_rgba(23,23,20,0.18)]">
          <Icon name="gift" size={38} />
        </div>
      )}

        <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-action">Добро пожаловать</p>
        <h1 className="mb-3 text-4xl font-extrabold tracking-[-0.06em] text-[#171714]">
          {showEmailForm && emailMode === "register" ? "Регистрация" : "Войти"}
        </h1>
        <p className="mb-10 text-center text-sm leading-6 text-muted">
          Координация подарков для всей семьи
        </p>

        {authError && (
          <div
            role="alert"
            className="mb-4 w-full rounded-2xl border border-[#F2C7C2] bg-[#FFF1EF] p-3 text-center text-sm text-[#A93027]"
          >
            {authError}
          </div>
        )}

        {showEmailForm ? (
          <EmailAuthForm mode={emailMode} onModeChange={setEmailMode} />
        ) : (
          <button
            type="button"
            onClick={() => setShowEmailForm(true)}
            className="mb-3 h-[52px] w-full rounded-2xl bg-action px-5 text-base font-bold text-white shadow-[0_10px_24px_rgba(194,65,12,0.24)] transition-transform hover:-translate-y-0.5 active:scale-[0.96]"
          >
            Войти по email
          </button>
        )}

        <div className="my-5 flex w-full items-center gap-3 text-xs text-muted" aria-hidden="true">
          <span className="h-px flex-1 bg-line" />
          или
          <span className="h-px flex-1 bg-line" />
        </div>

        <AuthProviderButtons />

        <p className="mt-6 text-center text-xs leading-relaxed text-muted">
          Регистрируясь, вы соглашаетесь с условиями использования и политикой конфиденциальности
        </p>
      </div>
  );
}

function EmailAuthForm({
  mode,
  onModeChange,
}: {
  mode: EmailMode;
  onModeChange: (mode: EmailMode) => void;
}) {
  const { loginWithEmail, registerWithEmail } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [requestError, setRequestError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const nameId = useId();
  const emailId = useId();
  const passwordId = useId();
  const nameErrorId = useId();
  const emailErrorId = useId();
  const passwordErrorId = useId();

  function changeMode(nextMode: EmailMode) {
    onModeChange(nextMode);
    setErrors({});
    setRequestError(null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    const normalizedEmail = email.trim();

    if (mode === "register" && !name.trim()) {
      nextErrors.name = "Введите имя.";
    }
    if (!normalizedEmail || !emailRef.current?.validity.valid) {
      nextErrors.email = "Введите корректный email.";
    }
    if (Array.from(password).length < 15) {
      nextErrors.password = "Пароль должен содержать не менее 15 символов.";
    } else if (new TextEncoder().encode(password).length > 72) {
      nextErrors.password = "Пароль должен занимать не более 72 байт.";
    }

    setErrors(nextErrors);
    setRequestError(null);
    if (Object.keys(nextErrors).length > 0) {
      (nextErrors.name
        ? nameRef
        : nextErrors.email
          ? emailRef
          : passwordRef
      ).current?.focus();
      return;
    }

    setIsSubmitting(true);
    try {
      if (mode === "register") {
        await registerWithEmail(name.trim(), normalizedEmail, password);
      } else {
        await loginWithEmail(normalizedEmail, password);
      }
    } catch (error) {
      if (error instanceof ApiError && error.code === "INVALID_CREDENTIALS") {
        setRequestError(INVALID_CREDENTIALS_ERROR);
      } else if (error instanceof ApiError && error.code === "REGISTRATION_FAILED") {
        setRequestError(REGISTRATION_ERROR);
      } else {
        setRequestError(REQUEST_ERROR);
      }
      setIsSubmitting(false);
    }
  }

  const submitLabel = mode === "register" ? "Зарегистрироваться" : "Войти";

  return (
    <div className="w-full">
      <div
        role="group"
        aria-label="Режим формы"
        className="mb-5 grid grid-cols-2 rounded-2xl bg-canvas p-1"
      >
        <button
          type="button"
          aria-pressed={mode === "login"}
          onClick={() => changeMode("login")}
          className={`min-h-11 rounded-xl px-3 text-sm font-semibold transition-colors ${
            mode === "login" ? "bg-white text-content shadow-sm" : "text-muted"
          }`}
        >
          Вход
        </button>
        <button
          type="button"
          aria-pressed={mode === "register"}
          onClick={() => changeMode("register")}
          className={`min-h-11 rounded-xl px-3 text-sm font-semibold transition-colors ${
            mode === "register" ? "bg-white text-content shadow-sm" : "text-muted"
          }`}
        >
          Регистрация
        </button>
      </div>

      <form onSubmit={handleSubmit} noValidate aria-busy={isSubmitting} className="space-y-4">
        {mode === "register" && (
          <AuthField
            ref={nameRef}
            id={nameId}
            label="Имя"
            name="name"
            value={name}
            onChange={(value) => {
              setName(value);
              if (errors.name) setErrors((current) => ({ ...current, name: "" }));
            }}
            autoComplete="name"
            error={errors.name}
            errorId={nameErrorId}
            disabled={isSubmitting}
          />
        )}
        <AuthField
          ref={emailRef}
          id={emailId}
          label="Email"
          name="email"
          type="email"
          value={email}
          onChange={(value) => {
            setEmail(value);
            if (errors.email) setErrors((current) => ({ ...current, email: "" }));
          }}
          autoComplete="email"
          error={errors.email}
          errorId={emailErrorId}
          disabled={isSubmitting}
          spellCheck={false}
        />
        <AuthField
          ref={passwordRef}
          id={passwordId}
          label="Пароль"
          name="password"
          type="password"
          value={password}
          onChange={(value) => {
            setPassword(value);
            if (errors.password) setErrors((current) => ({ ...current, password: "" }));
          }}
          autoComplete={mode === "register" ? "new-password" : "current-password"}
          error={errors.password}
          errorId={passwordErrorId}
          disabled={isSubmitting}
        />

        {requestError && (
          <p role="alert" className="rounded-xl bg-danger-soft px-3 py-2 text-sm text-danger">
            {requestError}
          </p>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="flex h-[52px] w-full items-center justify-center gap-2 rounded-2xl bg-action px-5 text-base font-bold text-white shadow-[0_10px_24px_rgba(194,65,12,0.24)] transition-[transform,background-color] hover:bg-action-hover active:scale-[0.96] disabled:cursor-wait disabled:bg-surface-disabled disabled:shadow-none"
        >
          {isSubmitting && (
            <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
          )}
          {submitLabel}
        </button>
      </form>
    </div>
  );
}

interface AuthFieldProps {
  ref: React.Ref<HTMLInputElement>;
  id: string;
  label: string;
  name: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: string;
  error?: string;
  errorId: string;
  disabled: boolean;
  spellCheck?: boolean;
}

function AuthField({
  ref,
  id,
  label,
  name,
  type = "text",
  value,
  onChange,
  autoComplete,
  error,
  errorId,
  disabled,
  spellCheck,
}: AuthFieldProps) {
  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-sm font-bold text-content">
        {label}
      </label>
      <input
        ref={ref}
        id={id}
        name={name}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete={autoComplete}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        disabled={disabled}
        spellCheck={spellCheck}
        className="form-control h-[52px] w-full rounded-2xl border border-line bg-white px-4 focus:outline-none"
      />
      <p id={errorId} className="mt-1 min-h-5 text-sm text-danger">
        {error ?? ""}
      </p>
    </div>
  );
}
